import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getLoadLineItemsForLoadHeader,
  updateVehicleTrackingLoadLineItem,
} from 'src/redux/Actions/VehicleTrackingOutward';
import MuiAlert from '@mui/material/Alert';

export default function VehicleTrackingOutwardLoadDetails({
  vtLoadHeaderUniqueNumber,
  toggleLoadDetailDrawer,
  isLoadEditing,
  setIsLoadEditing,
}) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;
  const themeColor = authReducer?.userData?.tenant?.theme;

  const { loadLineItemsForLoadHeader } = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );

  const [actualQtyMap, setActualQtyMap] = useState({});
  const [invalidFormSubmissionDetails, setInvalidFormSubmissionDetails] =
    useState({ isInvalid: false, message: '' });

  useEffect(() => {
    if (tenantOrganisationId && vtLoadHeaderUniqueNumber) {
      dispatch(
        getLoadLineItemsForLoadHeader(
          tenantOrganisationId,
          vtLoadHeaderUniqueNumber,
        ),
      );
    }
  }, [dispatch, tenantOrganisationId, vtLoadHeaderUniqueNumber]);

  useEffect(() => {
    if (loadLineItemsForLoadHeader?.lineItems) {
      const initialState = {};

      loadLineItemsForLoadHeader.lineItems.forEach((item) => {
        initialState[item.id] =
          item.actualQuantity ?? item.actual_quantity ?? '';
      });

      setActualQtyMap(initialState);
    }
  }, [loadLineItemsForLoadHeader]);

  const handleActualQtyChange = (value, item) => {
    let numericValue = Number(value);
    const receivingQuantity = item.receivingQuantity ?? item.receiving_quantity;
    if (numericValue > receivingQuantity) {
      numericValue = receivingQuantity;
    }
    setActualQtyMap((prev) => ({
      ...prev,
      [item.id]: value === '' ? '' : numericValue,
    }));
  };

  const validateForm = () => {
    const lineItems = loadLineItemsForLoadHeader?.lineItems ?? [];
    return lineItems.every((item) => {
      const qty = actualQtyMap[item.id];
      return qty !== '' && qty !== undefined;
    });
  };

  const handleSubmit = () => {
    const isValid = validateForm();

    if (!isValid) {
      setInvalidFormSubmissionDetails({
        isInvalid: true,
        message: 'Please fill all required fields before submitting.',
      });
      return;
    }

    setInvalidFormSubmissionDetails({
      isInvalid: false,
      message: '',
    });

    const lineItems =
      loadLineItemsForLoadHeader?.lineItems.map((item) => ({
        vehicle_tracking_load_line_item_id: item.id,
        actual_quantity: Number(actualQtyMap[item.id]),
      })) || [];

    const payload = {
      tenant_organisation_id: tenantOrganisationId,
      items: lineItems,
    };

    dispatch(updateVehicleTrackingLoadLineItem(payload)).then(() => {
      setActualQtyMap({});
      setInvalidFormSubmissionDetails({
        isInvalid: false,
        message: '',
      });
      setIsLoadEditing(false);
      toggleLoadDetailDrawer(false);
    });
  };

  return (
    <div className="page-layout div-flex-column right-form-layout">
      <div className="div-flex-row div-space-between div-align-center border-bottom drawer-section-padding px-3 py-2">
        <span className="text-xxl text-bold">
          {loadLineItemsForLoadHeader?.vehicleTrackingLoadCode}
        </span>
      </div>
      {invalidFormSubmissionDetails.isInvalid && (
        <MuiAlert
          onClose={() =>
            setInvalidFormSubmissionDetails({ isInvalid: false, message: '' })
          }
          severity="error"
          elevation={6}
          variant="filled"
          sx={{ mb: 2 }}
        >
          {invalidFormSubmissionDetails.message}
        </MuiAlert>
      )}
      <div
        className={`main-vehicle-tracking-detail-form-container px-3 py-3 theme-scrollable-container-${themeColor}`}
      >
        <div className="po-table-wrapper load-line-items-table-wrapper">
          <table>
            <thead className="outward-delivery-table-head">
              <tr>
                <th>Delivery Number</th>
                <th>Material Code</th>
                <th>Material Description</th>
                <th>Plant</th>
                <th>Delivery Quantity</th>
                <th>Pack Type</th>
                <th>Pack Quantity</th>
                <th>Actual Quantity</th>
              </tr>
            </thead>
            <tbody>
              {loadLineItemsForLoadHeader?.lineItems?.map((item) => (
                <tr key={item.id}>
                  <td>{item.sapCode || item.sap_code || '-'}</td>
                  <td>{item.product?.product_sap_code || '-'}</td>
                  <td>{item.product?.product_description || '-'}</td>
                  <td>{item.plant?.name || '-'}</td>
                  <td>{item.receiving_quantity ?? '-'}</td>
                  <td>{item.pack_type || '-'}</td>
                  <td>{item.pack_quantity ?? '-'}</td>
                  <td>
                    {isLoadEditing ? (
                      <input
                        className="form-input-section"
                        type="number"
                        min="0"
                        value={actualQtyMap[item.id] || ''}
                        onChange={(e) =>
                          handleActualQtyChange(e.target.value, item)
                        }
                      />
                    ) : (
                      item.actualQuantity ?? item.actual_quantity ?? '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="footer-container footer-padding">
        <div className="div-space-between div-align-center">
          <button
            type="button"
            className="common-btn-outlined"
            onClick={() => toggleLoadDetailDrawer(false)}
          >
            <span className="text-sm">Cancel</span>
          </button>

          {isLoadEditing && (
            <button
              type="button"
              className={`common-btn theme-background-${themeColor}`}
              onClick={handleSubmit}
            >
              <span className="text-sm">Submit</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
